import React, { useMemo, useState } from 'react';
import { rd } from '@passionware/monads';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import { Loading } from 'v1/components/shared';
import {
  getLegacyColumnWidthStyle,
  renderLegacyColumnCell
} from 'v1/components/shared/layout/Tables/legacyColumnCell';
import { TailwindRoot } from '~/v4/shared/TailwindRoot';
import {
  EntityEmptyState,
  SortableEntityTable
} from 'v5/patterns/entity-list/SortableEntityTable';
import { cn } from 'v5/platform/dom/cn';
import { rowLevelValidation } from 'v5/platform/row-level-validation';
import { myServices } from 'v5/services/services.connected.app';
import './SettingsTable.scss';

const queryUtils = {
  setSort: (query, sort) => ({ ...query, sort })
};

function resolveRowId(row, index, getSortableID) {
  if (getSortableID) return getSortableID(row);
  if (row?.id != null) return row.id;
  return `row-${index}`;
}

function toEntityColumns(columns) {
  return columns.map((column, index) => {
    // Always suffix with index so duplicate accessors (e.g. Duplicates /
    // BookingTypes) stay unique React keys in SortableEntityTable.
    const base = column.id ?? column.accessor ?? `col`;
    const field = `${String(base)}-${index}`;
    const widthStyle = getLegacyColumnWidthStyle(column);
    return {
      field,
      label: column.Header == null ? '' : String(column.Header),
      sortable: false,
      render: entity => {
        const content = renderLegacyColumnCell(column, entity, 0);
        if (!widthStyle) return content;
        return <div style={widthStyle}>{content}</div>;
      }
    };
  });
}

export default function SettingsTable({
  data = [],
  columns = [],
  sortable = false,
  onOrderChange,
  getSortableID,
  omitSortable,
  emptyState = {},
  loading = false,
  label,
  className
}) {
  const [query, setQuery] = useState({ sort: null });

  const entityColumns = useMemo(() => toEntityColumns(columns), [columns]);

  // Ensure every row has an `id` for SortableEntityTable; keep domain `id` when present.
  // Reorder keys may differ (see FieldsStructureType) via mapToValidatedRows getRowId.
  const entities = useMemo(
    () =>
      data.map((row, index) =>
        row?.id != null
          ? row
          : { ...row, id: resolveRowId(row, index, getSortableID) }
      ),
    [data, getSortableID]
  );

  const tableData = useMemo(
    () =>
      rd.of(
        rowLevelValidation.mapToValidatedRows(entities, (item, index) =>
          resolveRowId(item, index, getSortableID)
        )
      ),
    [entities, getSortableID]
  );

  if (loading) return <Loading />;

  return (
    <div className={cn('SettingsTable', className)}>
      {label ? <label>{label}</label> : null}
      {isEmpty(data) ? (
        <EntityEmptyState
          title={emptyState.title || 'Empty'}
          message={emptyState.description || 'No Results'}
        />
      ) : (
        <TailwindRoot className="min-w-0 w-full">
          <SortableEntityTable
            services={myServices}
            data={tableData}
            query={query}
            onQueryChange={setQuery}
            queryUtils={queryUtils}
            columns={entityColumns}
            getKey={row => getSortableID?.(row) ?? row.id}
            rowReorder={
              sortable
                ? {
                    enabled: true,
                    isItemDisabled: omitSortable
                      ? id => {
                          const row = entities.find(
                            (item, index) =>
                              resolveRowId(item, index, getSortableID) == id
                          );
                          return row ? !!omitSortable(row) : false;
                        }
                      : undefined,
                    onReorder: orderedIds => {
                      onOrderChange?.(orderedIds.map(String));
                    }
                  }
                : undefined
            }
          />
        </TailwindRoot>
      )}
    </div>
  );
}

SettingsTable.propTypes = {
  data: PropTypes.array,
  columns: PropTypes.array,
  sortable: PropTypes.bool,
  onOrderChange: PropTypes.func,
  getSortableID: PropTypes.func,
  omitSortable: PropTypes.func,
  emptyState: PropTypes.object,
  loading: PropTypes.bool,
  label: PropTypes.string,
  className: PropTypes.string
};
